import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from '@/lib/api-client';
import { readWithRetries } from '../api/read-with-retries';
import {
  placeFromRef,
  placeKey,
  type AdministrationScope,
  type Place,
  type PlaceDetail,
} from './place';

export type AdministrationScopesState =
  | { readonly status: 'loading'; readonly scopes: readonly [] }
  | { readonly status: 'ready'; readonly scopes: readonly AdministrationScope[] }
  /** The read did not answer after retries. Not the same as "administers nothing". */
  | { readonly status: 'unavailable'; readonly scopes: readonly [] };

export type AdministrationScopesValue = AdministrationScopesState & {
  /** Places the client has learned about, keyed by `placeKey` — from `/scopes` and from place reads. */
  readonly places: Readonly<Record<string, PlaceDetail>>;
  /** Only what place reads answered: a place in here needs no second read this session. */
  readonly placeReads: Readonly<Record<string, PlaceDetail>>;
  /** Records what a place read taught the client about a place. */
  readonly rememberPlace: (detail: PlaceDetail) => void;
  /** Drops what a place read taught, so the place is read again — after the caller's standing changed. */
  readonly forgetPlace: (place: Place) => void;
  /** Reads the scopes again — after `unavailable`, from the area's «Riprova». */
  readonly reload: () => void;
};

const ScopesContext = createContext<AdministrationScopesValue | null>(null);

/**
 * The places the signed-in person administers, read once per session (`/v1/administration/scopes`,
 * ADR-018 decision 3).
 *
 * Mounted beside `OperatorProvider` because the rail needs it too: the Amministrazione entry
 * appears for anyone who holds an administrator grant anywhere, and for nobody else (FR-AD-3). A
 * refusal or an empty list both resolve to `ready` with no scopes — the entry is simply absent.
 * Only a read that could not be answered resolves to `unavailable`, so the area can say so instead
 * of telling an administrator they have no rights.
 */
export function AdministrationScopesProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdministrationScopesState>({ status: 'loading', scopes: [] });
  const [learned, setLearned] = useState<Readonly<Record<string, PlaceDetail>>>({});
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', scopes: [] });
    void readWithRetries(() => apiClient.GET('/v1/administration/scopes'))
      .then((outcome) => {
        if (cancelled) {
          return;
        }
        if (outcome === 'unavailable') {
          setState({ status: 'unavailable', scopes: [] });
          return;
        }
        const { data, error, response } = outcome;
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'ready', scopes: [] });
          return;
        }
        setState({ status: 'ready', scopes: data });
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: 'unavailable', scopes: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const forgetPlace = useCallback((place: Place) => {
    setLearned((current) => {
      const next = { ...current };
      delete next[placeKey(place)];
      return next;
    });
  }, []);

  const rememberPlace = useCallback((detail: PlaceDetail) => {
    const place = placeFromRef(detail.place);
    if (place !== null) {
      setLearned((current) => ({ ...current, [placeKey(place)]: detail }));
    }
  }, []);

  // A granted place is known from its own scope entry; what a place read taught wins over it,
  // because the read's `can` is the union of every covering grant and the entry's is one grant's.
  const value = useMemo<AdministrationScopesValue>(() => {
    const places: Record<string, PlaceDetail> = {};
    for (const scope of state.scopes) {
      const place = placeFromRef(scope.place);
      if (place !== null && places[placeKey(place)] === undefined) {
        places[placeKey(place)] = { place: scope.place, tenant: scope.tenant, can: scope.can };
      }
    }
    return {
      ...state,
      places: { ...places, ...learned },
      placeReads: learned,
      rememberPlace,
      forgetPlace,
      reload,
    };
  }, [state, learned, rememberPlace, forgetPlace, reload]);

  return <ScopesContext.Provider value={value}>{children}</ScopesContext.Provider>;
}

export function useAdministrationScopes(): AdministrationScopesValue {
  const value = useContext(ScopesContext);
  if (value === null) {
    throw new Error('useAdministrationScopes must be used inside AdministrationScopesProvider');
  }
  return value;
}

/**
 * For the shell, which renders on screens that may mount without the provider in tests: absent
 * means "not known", which closes the gate.
 */
export function useAdministrationScopesOptional(): AdministrationScopesValue | undefined {
  return useContext(ScopesContext) ?? undefined;
}
